"use client";

import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";

const SHOW_DELAY_MS = 100;

type NavigationContextValue = {
  startNavigation: () => void;
};

const NavigationContext = createContext<NavigationContextValue | null>(null);

export function useNavigationStart(): () => void {
  return useContext(NavigationContext)?.startNavigation ?? (() => undefined);
}

function isInternalNavigation(anchor: HTMLAnchorElement): boolean {
  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
    return false;
  }
  if (anchor.target === "_blank" || anchor.hasAttribute("download")) {
    return false;
  }
  try {
    const url = new URL(anchor.href, window.location.href);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
}

function shouldStartNavigation(
  target: URL,
  pathname: string,
  searchParams: URLSearchParams,
): boolean {
  const currentSearch = searchParams.toString();
  const targetSearch = target.search.startsWith("?")
    ? target.search.slice(1)
    : target.search;
  const targetPath = target.pathname + (targetSearch ? `?${targetSearch}` : "");
  const currentPath = pathname + (currentSearch ? `?${currentSearch}` : "");
  return targetPath !== currentPath;
}

function PageTransitionLoaderInner({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [showOverlay, setShowOverlay] = useState(false);
  const isNavigatingRef = useRef(false);
  const showDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finishNavigation = useCallback(() => {
    isNavigatingRef.current = false;
    setShowOverlay(false);
    if (showDelayRef.current) {
      clearTimeout(showDelayRef.current);
      showDelayRef.current = null;
    }
  }, []);

  const startNavigation = useCallback(() => {
    isNavigatingRef.current = true;
    if (showDelayRef.current) {
      clearTimeout(showDelayRef.current);
    }
    showDelayRef.current = setTimeout(() => {
      if (isNavigatingRef.current) {
        setShowOverlay(true);
      }
    }, SHOW_DELAY_MS);
  }, []);

  useEffect(() => {
    finishNavigation();
  }, [pathname, searchParams, finishNavigation]);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const anchor = (event.target as Element | null)?.closest("a");
      if (!anchor || !isInternalNavigation(anchor)) return;

      const url = new URL(anchor.href, window.location.href);
      if (!shouldStartNavigation(url, pathname, searchParams)) return;

      startNavigation();
    }

    function handlePopState() {
      startNavigation();
    }

    document.addEventListener("click", handleClick, true);
    window.addEventListener("popstate", handlePopState);
    return () => {
      document.removeEventListener("click", handleClick, true);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [pathname, searchParams, startNavigation]);

  useEffect(() => {
    return () => {
      if (showDelayRef.current) {
        clearTimeout(showDelayRef.current);
      }
    };
  }, []);

  return (
    <NavigationContext.Provider value={{ startNavigation }}>
      {children}
      {showOverlay ? (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/70"
          role="status"
          aria-live="polite"
          aria-label="Loading page"
        >
          <div
            className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-brand-red"
            aria-hidden
          />
        </div>
      ) : null}
    </NavigationContext.Provider>
  );
}

export function PageTransitionLoader({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={children}>
      <PageTransitionLoaderInner>{children}</PageTransitionLoaderInner>
    </Suspense>
  );
}
